import type { Reference } from '../Metamodel/SupportTypes/Reference';
import type { SpecificAssetId } from '../Metamodel/SupportTypes/SpecificAssetId';
import type { PagingParams } from '../Rest/Interfaces/PagingParams';
import type { SerializationModifiers } from '../Rest/Interfaces/SerializationModifiers';
import { AasRequestError } from '../Rest/Client/AasRequestError';

export const assignSearchParams = (
  params: URLSearchParams,
  assign: Record<string, string | string[] | undefined>,
): void => {
  for (const key in assign) {
    const val = assign[key];
    if (Array.isArray(val)) {
      val.forEach(v => params.append(key, v));
    }
    else if (typeof val === 'string') {
      params.set(key, val);
    }
  }
};

export const assignPagingParams = (
  params: URLSearchParams,
  pagingParams?: PagingParams,
): void => {
  if (pagingParams) {
    const paging = {
      ...pagingParams,
      limit: pagingParams.limit ? String(pagingParams.limit) : undefined,
    };
    assignSearchParams(params, paging);
  }
};

export const assignSerializationModifiers = (
  params: URLSearchParams,
  serializationModifiers?: SerializationModifiers,
): void => {
  if (serializationModifiers) {
    assignSearchParams(params, serializationModifiers);
  }
};

export const base64Url = (str: string) => {
  const base64 = btoa(str);
  return base64
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
};

export const encodeSpecificAssetId = (id: SpecificAssetId): string => {
  const json = JSON.stringify(id);
  return base64Url(json);
};

export const encodeReference = (ref: Reference): string => {
  const json = JSON.stringify(ref);
  return base64Url(json);
};

export const assignSemanticId = (
  params: URLSearchParams,
  semanticId?: Reference,
): void => {
  if (semanticId) {
    const encoded = encodeReference(semanticId);
    const assign = {
      semanticId: encoded,
    };
    assignSearchParams(params, assign);
  }
};

export const assertValidAasResponse = async <T>(
  request: Request,
  response: Response,
  converserionType: 'json' | 'blob' | 'text' | 'void' = 'json',
): Promise<T> => {
  const status = response.status;
  if (status > 299) {
    let messages;
    try {
      messages = await response.json();
    }
    catch {
      messages = undefined;
    }
    throw new AasRequestError(request, response, messages);
  }
  let result;
  switch (converserionType) {
    case 'json':
      result = await response.json();
      break;
    case 'blob':
      result = await response.blob();
      break;
    case 'text':
      result = await response.text();
      break;
    default:
      result = undefined;
  }
  return result as T;
};

export const assignAssetParams = (
  params: URLSearchParams,
  assetIds?: SpecificAssetId[],
  idShort?: string,
): void => {
  const encoded = assetIds?.map(encodeSpecificAssetId);
  const assign = {
    assetIds: encoded,
    idShort,
  };
  assignSearchParams(params, assign);
};

export const assignReferences = (
  params: URLSearchParams,
  referencesMapping: Record<string, Reference | undefined>,
) => {
  const assign: Record<string, string | string[] | undefined> = {};
  for (const key in referencesMapping) {
    const ref = referencesMapping[key];
    if (ref) {
      const encoded = encodeReference(ref);
      assign[key] = encoded;
    }
  }
  assignSearchParams(params, assign);
};

/**
 * Safely joins URL segments, ensuring that there are no double slashes or missing slashes.
 * @param args URL segments to join
 * @returns joined URL
 */
export function urljoin(...args: string[]): string {
  let url = '';
  for (const arg of args) {
    if (url === '') {
      url = arg;
    }
    else {
      if (url.endsWith('/')) {
        url = url.slice(0, -1);
      }
      if (arg.startsWith('/')) {
        url += arg;
      }
      else {
        url += `/${arg}`;
      }
    }
  }
  return url;
}

export type HTTP_VERBS = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD';

export const headersLength = (headers: Headers): number => {
  let count = 0;
  headers.forEach(() => {
    count++;
  });
  return count;
};

export const LEISOFT_DOMAIN = 'leisoft.de';
export const LEISOFT_WWW_DOMAIN = 'www.leisoft.de';
export const LEISOFT_API_DOMAIN = 'api.leisoft.de';
export const LEISOFT_CLIENT_DOMAIN = 'client.leisoft.de';

export const IDTA_DOMAIN = 'admin-shell.io';
